import type { User } from "@book-club/shared";
import { useEffect, useState } from "react";
import { type BirthdayFilterState, nextBirthdayFilterState } from "@/features/members/constants/memberFilters";
import {
    applyMemberFilters,
    excludeArchivedMembers,
    type FilterType,
    filterMembers,
    getFilterCounts,
    type SortColumn,
    sortMembers,
} from "@/features/members/utils/memberUtils";

const PAGE_SIZE = 25;

// Define default sort direction for each column
const DEFAULT_SORT_DIRECTIONS: Record<SortColumn, "asc" | "desc"> = {
    cancellations: "desc",
    lastAttended: "desc",
    lateCancellations: "desc",
    monthsSinceLastAttendance: "desc",
    name: "asc",
    phone: "asc",
    totalAttended: "desc",
    totalNoShows: "desc",
};

type UseMemberDirectoryReturn = {
    // State
    searchQuery: string;
    sortColumn: SortColumn;
    sortDirection: "asc" | "desc";
    currentPage: number;
    activeFilters: Set<FilterType>;
    filterCounts: Record<FilterType, number>;
    birthdayFilter: BirthdayFilterState;

    // Actions
    setSearchQuery: (query: string) => void;
    handleSort: (column: SortColumn) => void;
    setCurrentPage: (page: number) => void;
    toggleFilter: (filterType: FilterType) => void;
    toggleBirthdayFilter: () => void;
    clearFilters: () => void;

    // Computed values
    filteredMembers: User[];
    sortedMembers: User[];
    paginatedMembers: User[];
    totalPages: number;
    startIndex: number;
    endIndex: number;
};

/**
 * useMemberDirectory hook encapsulates all business logic and state management
 * for the Member Directory page
 */
export function useMemberDirectory(members: User[]): UseMemberDirectoryReturn {
    const [searchQuery, setSearchQuery] = useState("");
    const [sortColumn, setSortColumn] = useState<SortColumn>("name");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
    const [currentPage, setCurrentPage] = useState(1);
    const [activeFilters, setActiveFilters] = useState<Set<FilterType>>(new Set());
    const [birthdayFilter, setBirthdayFilter] = useState<BirthdayFilterState>("off");

    // Calculate filter counts from full members list (excluding archived as base)
    const nonArchivedMembers = excludeArchivedMembers(members);
    const filterCounts = getFilterCounts(nonArchivedMembers);

    // Combine chip filters with birthday toggle into a single set
    const combinedFilters = new Set(activeFilters);
    if (birthdayFilter !== "off") {
        combinedFilters.add(birthdayFilter);
    }

    // Filter and sort members
    // Order: exclude archived (base filter) -> search -> member filters -> sort
    const searchFiltered = filterMembers(nonArchivedMembers, searchQuery);
    const memberFiltered = applyMemberFilters(searchFiltered, combinedFilters);
    const sortedMembers = sortMembers(memberFiltered, sortColumn, sortDirection);

    // Paginate
    const totalPages = Math.ceil(sortedMembers.length / PAGE_SIZE);
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    const paginatedMembers = sortedMembers.slice(startIndex, endIndex);

    // Reset to page 1 when search or sort changes
    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(1);
        }
    }, [currentPage, totalPages]);

    // Reset to page 1 when search query or filters change
    useEffect(() => {
        setCurrentPage(1);
    }, []);

    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortDirection(DEFAULT_SORT_DIRECTIONS[column]);
        }
        setCurrentPage(1);
    };

    const toggleFilter = (filterType: FilterType) => {
        setActiveFilters((prev) => {
            const next = new Set(prev);
            if (next.has(filterType)) {
                next.delete(filterType);
            } else {
                next.add(filterType);
            }
            return next;
        });
    };

    const toggleBirthdayFilter = () => {
        setBirthdayFilter((prev) => nextBirthdayFilterState(prev));
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setActiveFilters(new Set());
        setBirthdayFilter("off");
    };

    return {
        activeFilters,
        birthdayFilter,
        clearFilters,
        currentPage,
        endIndex,
        filterCounts,
        filteredMembers: memberFiltered,
        handleSort,
        paginatedMembers,
        searchQuery,
        setCurrentPage,
        setSearchQuery,
        sortColumn,
        sortDirection,
        sortedMembers,
        startIndex,
        toggleBirthdayFilter,
        toggleFilter,
        totalPages,
    };
}
